import React, { useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  titulo: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
}

const Modal: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  titulo, 
  children, 
  size = 'md',
  showCloseButton = true 
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl'
  };

  const handleEscapePress = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  const handleBackdropClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleEscapePress);
    } else {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleEscapePress);
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleEscapePress);
    };
  }, [isOpen, handleEscapePress]);

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        duration: 0.2
      }
    },
  };

  const modalVariants = {
    hidden: { 
      scale: 0.8, 
      opacity: 0,
      y: 20
    },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 30,
        stiffness: 400,
        duration: 0.3
      }
    },
    exit: { 
      scale: 0.8, 
      opacity: 0,
      y: 20,
      transition: {
        duration: 0.2
      }
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center text-white justify-center p-4 bg-black/60 backdrop-blur-md"
          onClick={handleBackdropClick}
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={backdropVariants}
          aria-modal="true"
          role="dialog"
          aria-labelledby="modal-titulo"
        >
          <motion.div
            className={`relative w-full ${sizeClasses[size]} bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl border border-gray-700/50 overflow-hidden`}
            ref={modalRef}
            variants={modalVariants}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header com gradiente */}
            <div className="relative bg-gradient-to-r from-primary/20 to-accent/20 border-b border-gray-700/50">
              <div className="absolute inset-0 bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663] backdrop-blur-sm" />
              <div className="relative flex items-center justify-between p-6">
                <div>
                  <h2 
                    id="modal-titulo" 
                    className="text-2xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-black"
                  >
                    {titulo}
                  </h2>
                  <div className="w-12 h-1 bg-gradient-to-r from-primary to-accent rounded-full mt-2" />
                </div>
                
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className="group relative p-2 bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663] text-black transition-all duration-200 hover:bg-gray-700/50 rounded-xl"
                    aria-label="Fechar modal"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663] rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 relative z-10"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Content Area */}
            <div className="max-h-[70vh] bg-background overflow-y-auto custom-scrollbar">
              <div className="p-1 text-gray-100">
                {children}
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-primary to-accent" />
            <div className="absolute bottom-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mb-12 blur-xl" />
            <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663]/5 rounded-full -ml-12 -mt-12 blur-xl" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Adicione este CSS global para uma scrollbar customizada
const globalStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(to bottom, var(--primary), var(--accent));
    border-radius: 3px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(to bottom, var(--primary), var(--accent));
    opacity: 0.8;
  }
`;

// Adicione os estilos globais no seu aplicativo
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.innerText = globalStyles;
  document.head.appendChild(styleSheet);
}

export default Modal;